import { ProjectPlanCumulation } from '../../../../lib/functions/projectPlan'
import {
  ProgramManagementProductivity,
  ProgramManagementProfitLoss,
  ProgramManagementProgress,
  ProgramManagementProgressSet,
  ProgramManagementResource,
  ProgramManagementRow,
  PROGRAM_MANAGEMENT_ROW_TYPE,
} from '../programManagement'

// API のレスポンス各フィールドは number | null（未設定の項目は欠落=undefined のこともある）。
// ?? 0 で補正し、s + undefined による NaN/クラッシュを防ぐ。
const sumProgress = (
  items: ProgramManagementProgress[]
): ProgramManagementProgress => ({
  total: items.reduce((s, p) => s + (p.total ?? 0), 0),
  scheduledCompleted: items.reduce(
    (s, p) => s + (p.scheduledCompleted ?? 0),
    0
  ),
  completed: items.reduce((s, p) => s + (p.completed ?? 0), 0),
  delayed: items.reduce((s, p) => s + (p.delayed ?? 0), 0),
  preceding: items.reduce((s, p) => s + (p.preceding ?? 0), 0),
})

// progress 配下のサブオブジェクト（deliverableCount 等）は報告の入力単位（個数/工数）に
// 応じて欠落する。値を持つ子だけで合算し、どの子も持たない単位は undefined のままにする。
// 0 埋めすると「未報告」が「0」として表示されてしまう（例: 工数のみ報告→個数表示で0）。
const sumProgressIfAny = (
  items: (ProgramManagementProgress | null | undefined)[]
): ProgramManagementProgress | undefined => {
  const present = items.filter((p): p is ProgramManagementProgress => p != null)
  return present.length > 0 ? sumProgress(present) : undefined
}

const sumProgressSet = (
  children: ProgramManagementRow[]
): ProgramManagementProgressSet | undefined => {
  const withProgress = children.filter(c => c.progress)
  if (withProgress.length === 0) return undefined
  return {
    deliverableCount: sumProgressIfAny(
      withProgress.map(c => c.progress!.deliverableCount)
    ),
    deliverableWorkload: sumProgressIfAny(
      withProgress.map(c => c.progress!.deliverableWorkload)
    ),
    taskCount: sumProgressIfAny(withProgress.map(c => c.progress!.taskCount)),
    taskWorkload: sumProgressIfAny(
      withProgress.map(c => c.progress!.taskWorkload)
    ),
  }
}

const sumResource = (
  children: ProgramManagementRow[]
): ProgramManagementResource | undefined => {
  const withResource = children.filter(c => c.resource)
  if (withResource.length === 0) return undefined
  return {
    totalResource: withResource.reduce(
      (s, c) => s + (c.resource!.totalResource ?? 0),
      0
    ),
    investedResource: withResource.reduce(
      (s, c) => s + (c.resource!.investedResource ?? 0),
      0
    ),
    scheduledResource: withResource.reduce(
      (s, c) => s + (c.resource!.scheduledResource ?? 0),
      0
    ),
    actualResource: withResource.reduce(
      (s, c) => s + (c.resource!.actualResource ?? 0),
      0
    ),
  }
}

// 投入工数(actualHour)は工程行/プロジェクト行に値が入る。PROGRAM/GROUP 行は値を
// 持たないため、直下の子の投入工数を合算する（resource とは別系統の集計）。
const sumProductivity = (
  children: ProgramManagementRow[]
): ProgramManagementProductivity | undefined => {
  const withProductivity = children.filter(c => c.productivity)
  if (withProductivity.length === 0) return undefined
  return {
    actualHour: withProductivity.reduce(
      (s, c) => s + (c.productivity!.actualHour ?? 0),
      0
    ),
  }
}

const sumCumulation = (
  children: ProgramManagementRow[]
): ProjectPlanCumulation | undefined => {
  const cumulations = children
    .map(c => c.projectPlanCumulation)
    .filter((c): c is ProjectPlanCumulation => c !== undefined)
  if (cumulations.length === 0) return undefined
  return ProjectPlanCumulation.sum(cumulations)
}

/**
 * 配下の最大予定終了日（超過＝黄色ハッチ表示用）を求める。
 * 子自身の予定終了日と、子の cumulation が持つ最大予定終了日（さらに配下分）の最大値。
 * ProjectPlanCumulation.sum は日付を集計しないため別途算出する。
 */
const maxScheduledEndDateOf = (
  children: ProgramManagementRow[]
): string | undefined => {
  const dates: string[] = []
  for (const child of children) {
    const ownEnd = child.scheduledDate?.endDate
    if (ownEnd) dates.push(ownEnd.replace(/\//g, '-'))
    const childMax = child.projectPlanCumulation?.maxScheduledEndDate
    if (childMax) dates.push(childMax)
  }
  if (dates.length === 0) return undefined
  return dates.sort()[dates.length - 1]
}

const sumProfitLoss = (
  children: ProgramManagementRow[]
): ProgramManagementProfitLoss | undefined => {
  const withProfitLoss = children.filter(c => c.profitLoss)
  if (withProfitLoss.length === 0) return undefined
  return {
    totalBudget: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.totalBudget ?? 0),
      0
    ),
    scheduledUsedBudget: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.scheduledUsedBudget ?? 0),
      0
    ),
    actualUsedBudget: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.actualUsedBudget ?? 0),
      0
    ),
    remainingBudget: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.remainingBudget ?? 0),
      0
    ),
    totalRevenue: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.totalRevenue ?? 0),
      0
    ),
    scheduledRevenue: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.scheduledRevenue ?? 0),
      0
    ),
    actualRevenue: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.actualRevenue ?? 0),
      0
    ),
    forecastRevenue: withProfitLoss.reduce(
      (s, c) => s + (c.profitLoss!.forecastRevenue ?? 0),
      0
    ),
  }
}

/**
 * 子の予定日から親の予定日を集計する。
 * 子の startDate の最小値 → 親の startDate
 * 子の endDate の最大値 → 親の endDate
 */
export const aggregateScheduledDate = (
  children: ProgramManagementRow[]
): { startDate?: string; endDate?: string } | undefined => {
  const dates = children.filter(c => c.scheduledDate).map(c => c.scheduledDate!)

  if (dates.length === 0) return undefined

  const startDates = dates.map(d => d.startDate).filter((d): d is string => !!d)
  const endDates = dates.map(d => d.endDate).filter((d): d is string => !!d)

  return {
    startDate: startDates.length > 0 ? startDates.sort()[0] : undefined,
    endDate:
      endDates.length > 0 ? endDates.sort()[endDates.length - 1] : undefined,
  }
}

/**
 * 予定日を配下から集計すべき行かどうか。
 * 原則は各行が持つ自身の予定日を表示する（PROJECT/WORKGROUP/PROCESS は常に自身の予定日）。
 * PROGRAM/GROUP のみ、自身の予定日が未設定の場合に配下から集計する。
 */
/** 予定日を配下から補完できる行か（PROGRAM/GROUP は自身の予定日が任意のため補完対象） */
const canAggregateScheduledDate = (row: ProgramManagementRow): boolean =>
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM ||
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP

// PROGRAM/GROUP 行は progress 等の集計値を自身では持たず、常に配下からの集計となる。
// 前回の集計結果が入力データに残っているため、「値があればスキップ」の判定だと
// 行追加・移動・削除後の再集計が行われない（移動元の減算漏れ等）。常に集計し直す。
const isAggregationDerivedRow = (row: ProgramManagementRow): boolean =>
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.PROGRAM ||
  row.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP

// グループ行のステータス集計で扱うプロジェクトステータス。
const AGGREGATABLE_STATUS = {
  STANDBY: 'STANDBY',
  INPROGRESS: 'INPROGRESS',
  FINISHED: 'FINISHED',
  CANCELED: 'CANCELED',
} as const

/**
 * グループ行のステータスを配下（直下の子）のステータスから集計する。
 * ボトムアップ処理により、子グループは既に集計済みのステータスを持つため、
 * 直下の子（プロジェクト＋子グループ）のステータスを見れば配下プロジェクト全体を表せる。
 *
 * - CANCELED は集計対象から除外し、全てが CANCELED のときのみ CANCELED
 * - INPROGRESS: INPROGRESS が1つ以上、または STANDBY と FINISHED が混在
 * - STANDBY: STANDBY が1つ以上、かつ INPROGRESS/FINISHED が無い
 * - FINISHED: FINISHED が1つ以上、かつ STANDBY/INPROGRESS が無い
 */
const aggregateGroupStatus = (
  children: ProgramManagementRow[]
): string | undefined => {
  const statuses = children.map(c => c.status).filter((s): s is string => !!s)
  // 配下にステータスを持つ行が無い（＝実質何もない）グループは STANDBY とする
  if (statuses.length === 0) return AGGREGATABLE_STATUS.STANDBY

  const active = statuses.filter(s => s !== AGGREGATABLE_STATUS.CANCELED)
  if (active.length === 0) return AGGREGATABLE_STATUS.CANCELED

  const hasInProgress = active.includes(AGGREGATABLE_STATUS.INPROGRESS)
  const hasStandby = active.includes(AGGREGATABLE_STATUS.STANDBY)
  const hasFinished = active.includes(AGGREGATABLE_STATUS.FINISHED)

  if (hasInProgress || (hasStandby && hasFinished)) {
    return AGGREGATABLE_STATUS.INPROGRESS
  }
  if (hasStandby) return AGGREGATABLE_STATUS.STANDBY
  if (hasFinished) return AGGREGATABLE_STATUS.FINISHED
  return undefined
}

/**
 * フラットな行配列に対してボトムアップ集計を行う。
 * treeValue から親子関係を復元し、深い行から順に処理することで
 * 子の集計値が親に正しく伝播する。
 *
 * - scheduledDate: GROUP/PROGRAM行で手動入力されていなければ子から集計
 * - progress: 自身に値がなければ直下の子から集計
 * - resource: 自身に値がなければ直下の子から集計
 * - profitLoss: 自身に値がなければ直下の子から集計
 */
export const aggregateTreeData = (
  rows: ProgramManagementRow[]
): ProgramManagementRow[] => {
  // treeValue から親子関係を構築
  const childrenByParentUuid = new Map<string, string[]>()
  for (const row of rows) {
    if (row.treeValue.length >= 2) {
      const parentUuid = row.treeValue[row.treeValue.length - 2]
      const siblings = childrenByParentUuid.get(parentUuid) ?? []
      siblings.push(row.uuid)
      childrenByParentUuid.set(parentUuid, siblings)
    }
  }

  // uuid → row のルックアップ（集計結果を格納する mutable コピー）
  const rowMap = new Map<string, ProgramManagementRow>(
    rows.map(r => [r.uuid, { ...r }])
  )

  // 深い行から順に処理（ボトムアップ）
  const sortedRows = [...rows].sort(
    (a, b) => b.treeValue.length - a.treeValue.length
  )

  for (const row of sortedRows) {
    const childUuids = childrenByParentUuid.get(row.uuid)
    if (!childUuids || childUuids.length === 0) {
      // 配下が無い行は集計元が無く cumulation が undefined になる。
      // ガント描画等が常に cumulation 存在を前提に動けるよう、タイプを問わず空の cumulation を保証する。
      const leaf = rowMap.get(row.uuid)!
      // PROGRAM/GROUP 行の集計値は配下由来のため、配下が無くなった（子が移動/削除された）
      // 場合は前回の集計残骸をクリアする。残すと移動元グループに減算前の値が表示され続ける。
      if (isAggregationDerivedRow(leaf)) {
        leaf.progress = undefined
        leaf.resource = undefined
        leaf.productivity = undefined
        leaf.profitLoss = undefined
        leaf.projectPlanCumulation = new ProjectPlanCumulation()
        // 配下から補完していた予定日もクリアする（自身で入力した値は保持）
        const ownStart = leaf.scheduledStartDateAggregated
          ? undefined
          : leaf.scheduledDate?.startDate
        const ownEnd = leaf.scheduledEndDateAggregated
          ? undefined
          : leaf.scheduledDate?.endDate
        leaf.scheduledDate =
          ownStart || ownEnd
            ? { startDate: ownStart, endDate: ownEnd }
            : undefined
        leaf.scheduledStartDateAggregated = false
        leaf.scheduledEndDateAggregated = false
      }
      if (!leaf.projectPlanCumulation) {
        leaf.projectPlanCumulation = new ProjectPlanCumulation()
      }
      // 配下に何もないグループ行のステータスは STANDBY とする
      if (leaf.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP) {
        leaf.status = AGGREGATABLE_STATUS.STANDBY
      }
      continue
    }

    const children = childUuids
      .map(uuid => rowMap.get(uuid))
      .filter((r): r is ProgramManagementRow => r !== undefined)

    const updated = rowMap.get(row.uuid)!

    // 予定日: 原則は各行が持つ自身の予定日を表示する。
    // PROGRAM/GROUP は自身の値が無いフィールド（開始/終了それぞれ）のみ配下から補完し、
    // 補完したフィールドにはフラグを立てて表示側で薄グレー文字にする。
    // 補完値は「自身の値」とみなさず、毎回集計し直す（配下の変更追従・クリア残骸防止）。
    if (canAggregateScheduledDate(updated)) {
      const aggregated = aggregateScheduledDate(children)
      const ownStart = updated.scheduledStartDateAggregated
        ? undefined
        : updated.scheduledDate?.startDate
      const ownEnd = updated.scheduledEndDateAggregated
        ? undefined
        : updated.scheduledDate?.endDate
      const startDate = ownStart ?? aggregated?.startDate
      const endDate = ownEnd ?? aggregated?.endDate
      updated.scheduledDate =
        startDate || endDate ? { startDate, endDate } : undefined
      updated.scheduledStartDateAggregated = !ownStart && !!startDate
      updated.scheduledEndDateAggregated = !ownEnd && !!endDate
    } else {
      updated.scheduledStartDateAggregated = false
      updated.scheduledEndDateAggregated = false
    }

    // グループ行は配下プロジェクトのステータスを集計して表示する
    if (updated.type === PROGRAM_MANAGEMENT_ROW_TYPE.GROUP) {
      updated.status = aggregateGroupStatus(children)
    }

    // PROGRAM/GROUP は前回の集計値を「自身の値」とみなさず毎回集計し直す（行追加・移動・削除への追従）。
    // それ以外（PROJECT/WORKGROUP）は API 由来の自身の値を優先し、無い場合のみ配下から補完する。
    const alwaysAggregate = isAggregationDerivedRow(updated)
    if (alwaysAggregate || !updated.progress) {
      updated.progress = sumProgressSet(children)
    }
    if (alwaysAggregate || !updated.resource) {
      updated.resource = sumResource(children)
    }
    if (alwaysAggregate || !updated.productivity) {
      updated.productivity = sumProductivity(children)
    }
    if (alwaysAggregate || !updated.profitLoss) {
      updated.profitLoss = sumProfitLoss(children)
    }
    if (alwaysAggregate || !updated.projectPlanCumulation) {
      // 子に cumulation が無く集計結果が空でも、cumulation は必ず存在させる。
      updated.projectPlanCumulation =
        sumCumulation(children) ?? new ProjectPlanCumulation()
    }
    // 超過線（配下の最大予定終了日）は、API 由来の cumulation を持つ行
    // （PROJECT/WORKGROUP）でも表示する必要があるため、cumulation の有無に関わらず
    // 常に配下から再計算して設定する。if の中だけだと PROJECT/WORKGROUP で欠落する。
    updated.projectPlanCumulation.maxScheduledEndDate =
      maxScheduledEndDateOf(children)
  }

  // 元の配列順序を維持して返す
  return rows.map(r => rowMap.get(r.uuid) ?? r)
}
